<script setup>
import Vue, {provide, ref, watch} from "vue";
import multiselect from "vue-multiselect";
import trackersCompaniesService from "@/services/trackers-companies.service";
import {getLogo} from "@/helpers/logo";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import trackersService from "@/services/trackers.service";
import TrackerQuestionsFill from "@/components/trackers/TrackerQuestionsFill.vue";
import AddStartup from "@/components/startups/import/AddStartups.vue";

const {opportunityId} = defineProps(['opportunityId']);
const emit = defineEmits(['startupAdded']);

const showModal = ref(false);
const startupsList = ref([]);
const defaultStartupsList = ref(null);
const loading = ref(false);
const adding = ref(false);
const startup = ref(null);
const questions = ref([]);
const trackerQuestionsFill = ref(null);

const addStartupModal = ref(null);

watch(startup, (value) => {
    if (value && value.id === -1) {
        showModal.value = false;
        addStartupModal.value.open();
    }
});

async function getStartupsList(search = '') {
    if (search || defaultStartupsList.value === null) {
        loading.value = true;
        const {data} = await trackersCompaniesService.getStartupsListToAdd(opportunityId, search);

        loading.value = false;
        startupsList.value = [...data];

        if (defaultStartupsList.value === null) {
            defaultStartupsList.value = [...data];
        }
    } else {
        startupsList.value = [...defaultStartupsList.value];
    }

    if (!search) {
        startupsList.value.push({
            id: -1,
            name: 'Add New Startup',
        })
    }
}

async function getQuestions() {
    const {data} = await trackersService.getQuestions(opportunityId);

    questions.value = data.map(question => ({
        ...question,
        error: '',
    }));
}

async function save() {
    if (!questions.value.length) {
        send();
        return;
    }

    trackerQuestionsFill.value.validateAnswers();

    if (questions.value.some(question => question.error.length)) {
        return;
    }

    adding.value = true;

    if (trackerQuestionsFill.value.newFilesFieldsCount) {
        trackerQuestionsFill.value.saveFiles();
    } else {
        send();
    }
}

async function send() {
    const {data} = await trackersCompaniesService.addCompanyToSharedTracker(opportunityId, startup.value.id, questions.value);

    adding.value = false;
    showModal.value = false;
    emit('startupAdded');

    if (!data) {
        Vue.notify({
            group: 'app',
            type: 'error',
            duration: 5000,
            text: "Startup already existed in tracker"
        })
    }
}

function addStartupModalClosed() {
    showModal.value = true;

    if (startup.value.id === -1) {
        startup.value = null;
    }
}

function updateStartup(newStartup) {
    startup.value = newStartup;
}

function open() {
    showModal.value = true;
    startup.value = null;
    defaultStartupsList.value = null;
    questions.value = [];
    getStartupsList();
    getQuestions();
}

provide('questions', questions);

defineExpose({
    open
});
</script>

<template>
    <div>
        <b-modal v-model="showModal" title="Submit Startup">
            <div class="form">
                <multiselect
                    class="multiselect--complex multiselect-mid"
                    v-model="startup"
                    :options="startupsList"
                    select-label=""
                    deselect-label=""
                    label="name"
                    track-by="name"
                    placeholder="Search startup"
                    @search-change="getStartupsList"
                    :loading="loading"
                >
                    <template slot="option" slot-scope="props">
                        <template v-if="props.option.id === -1">
                            <span class="link-add">Add New Startup</span>
                        </template>

                        <template v-else>
                            <img :src="getLogo(props.option.logo)" style="width:40px" alt=""> {{ props.option.name }}
                        </template>
                    </template>
                </multiselect>
            </div>

            <TrackerQuestionsFill
                v-if="questions.length && startup && startup.id > 0"
                ref="trackerQuestionsFill"
                :tracker-id="opportunityId"
                :company-id="startup.id"
                class="mt-5"
                @success="send"
                @failed="adding = false"
            />

            <template #modal-footer>
                <PrimaryButton
                    caption="Add To Project"
                    loadingCaption="Adding Startups..."
                    :disabled="!startup"
                    :loading="adding"
                    @click="save"
                />
            </template>
        </b-modal>

        <AddStartup
            ref="addStartupModal"
            :allow-multiple="false"
            @startupsAdded="updateStartup"
            @closed="addStartupModalClosed"
        />
    </div>
</template>

<style scoped>

</style>
